import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { rtkBaseQuery } from '../helpers/rtkquery';

export const deskApi = createApi({
  reducerPath: 'deskApi',
  baseQuery: rtkBaseQuery('/filems/'),
  tagTypes: ['FileItem', 'Folder', 'SharedFile', 'SharedFolder', 'Application'],
  endpoints: (builder) => ({
    viewFileToBase64: builder.query({
      query: (path) => `/Application/ViewApplicationFile?path=${path}`,
    }),
    getStaffFolderStructure: builder.query({
      query: (args) => {
        const folderId = args?.folderId;
        return folderId
          ? `/Folder/GetStaffStructure?folderId=${folderId}`
          : `/Folder/GetStaffStructure`;
      },
      invalidatesTags: ['Folder'],
    }),

    getAllTrash: builder.query({
      query: () => `/Folder/GetStaffStructureInTrash`,
      invalidatesTags: ['Folder'],
    }),

    createNewFolder: builder.mutation({
      query: (body) => ({
        url: '/Folder/CreateFolder',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Folder'],
    }),

    deleteFolder: builder.mutation({
      query: ({ id }) => ({
        url: `/Folder/DeleteFolder/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Folder'],
    }),

    sendFolderGroupToTrash: builder.mutation({
      query: (body) => ({
        url: '/Folder/SendFoldersByIdsToTrash',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['Folder'],
    }),

    uploadFile: builder.mutation({
      query: ({ folderId, formData }) => ({
        url: folderId ? `/FileItem/UploadFile?folderId=${folderId}` : `/FileItem/UploadFile`,
        method: 'POST',
        body: formData,
      }),
    }),

    getPathToRootFolder: builder.query({
      query: ({ folderId }) =>
        folderId ? `/Folder/GetParentFolders?folderId=${folderId}` : `/Folder/GetParentFolders`,
      invalidatesTags: ['Folder'],
    }),

    getFolderFromTrash: builder.query({
      query: () => '/Folder/GetAllTrashFolders',
      invalidatesTags: ['Folder'],
    }),

    restoreFolderFromTrash: builder.mutation({
      query: (id) => ({
        url: `/Folder/RestoreFolderFromTrash/${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['Folder'],
    }),

    restoreFolderGroupFromTrash: builder.mutation({
      query: (body) => ({
        url: '/Folder/RestoreFoldersByIdsFromTrash',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Folder'],
    }),

    sendFolderToTrash: builder.mutation({
      query: (id) => ({
        url: `/Folder/SendFolderToTrash/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Folder'],
    }),

    updateFolderName: builder.mutation({
      query: ({ id, body }) => ({
        url: `/Folder/UpdateFolder/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Folder'],
    }),

    getFolderById: builder.query({
      query: (id) => `/Folder/GetFolderById/${id}`,
      invalidatesTags: ['Folder'],
    }),

    sendFileToTrash: builder.mutation({
      query: (id) => ({
        url: `/FileItem/SendFileToTrash/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FileItem'],
    }),

    sendFileGroupToTrash: builder.mutation({
      query: (body) => ({
        url: '/FileItem/SendFileToTrashByIds',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FileItem'],
    }),

    restoreFileFromTrash: builder.mutation({
      query: (id) => ({
        url: `/FileItem/RestoreFileFromTrash/${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['FileItem'],
    }),

    restoreFilesGroupFromTrash: builder.mutation({
      query: (body) => ({
        url: '/FileItem/RestoreFileFromTrashByIds',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FileItem'],
    }),

    getFilesFromTrash: builder.query({
      query: () => '/FileItem/GetAllTrashFiles',
      invalidatesTags: ['FileItem'],
    }),

    deleteFileFromTrash: builder.mutation({
      query: (id) => ({
        url: `/FileItem/DeleteFile/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FileItem'],
    }),

    deleteFileGroupFromTrash: builder.mutation({
      query: (body) => ({
        url: '/FileItem/DeleteFilesByIds',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['FileItem'],
    }),

    deleteFolderFromTrash: builder.mutation({
      query: (id) => ({
        url: `/Folder/DeleteFolder/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Folder'],
    }),

    deleteFolderGroupFromTrash: builder.mutation({
      query: (body) => ({
        url: '/Folder/DeleteFolderByIds',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['Folder'],
    }),
    downloadFolderAsZip: builder.query({
      query: (folderId) => ({
        url: `/FileItem/DownloadFolderAsZip/${folderId}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
        responseType: 'blob',
      }),
    }),
    downloadFile: builder.query({
      query: (id) => ({
        url: `/FileItem/DownloadStaffFile/${id}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
        responseType: 'blob',
      }),
    }),
    shareFolder: builder.mutation({
      query: (body) => ({
        url: `/SharedFolder/StartShareFolder`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SharedFolder'],
    }),
    shareFile: builder.mutation({
      query: (params) => ({
        url: '/SharedFile/StartShareFile',
        method: 'POST',
        params,
      }),
      invalidatesTags: ['SharedFile'],
    }),

    stopShareFolder: builder.mutation({
      query: (body) => ({
        url: `/SharedFolder/StopShareAllFolders`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SharedFolder'],
    }),

    stopShareFiles: builder.mutation({
      query: (body) => ({
        url: `/SharedFile/StopShareAllFiles`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SharedFile'],
    }),

    getFolderSharedUsers: builder.query({
      query: (id) => ({
        url: `/SharedFolder/GetSharedFolderUserNames?fileId=${id}`,
      }),
      invalidatesTags: ['SharedFolder'],
    }),

    getFileSharedUsers: builder.query({
      query: (id) => ({
        url: `/SharedFile/GetSharedFileUserNames?fileId=${id}`,
      }),
      invalidatesTags: ['SharedFile'],
    }),

    uploadMultiPartFile: builder.mutation({
      query: (params) => ({
        url: '/FileItem/CreateMultipartUpload',
        method: 'POST',
        params,
      }),
    }),
    uploadFilePart: builder.mutation({
      query: ({ params, body }) => ({
        url: 'FileItem/UploadPart',
        method: 'POST',
        body,
        params,
      }),
    }),
    completedUploadFile: builder.mutation({
      query: (body) => ({
        url: '/FileItem/CompleteMultipartUpload',
        method: 'POST',
        body,
      }),
    }),
    cancelUploadFile: builder.mutation({
      query: (params) => ({
        url: '/FileItem/CancelUpload',
        method: 'POST',
        params,
      }),
    }),
  }),
});

export const {
  useLazyViewFileToBase64Query,
  useGetStaffFolderStructureQuery,
  useGetAllTrashQuery,
  useCreateNewFolderMutation,
  useDeleteFolderMutation,
  useSendFolderGroupToTrashMutation,
  useUploadFileMutation,
  useGetPathToRootFolderQuery,
  useGetFolderFromTrashQuery,
  useRestoreFolderFromTrashMutation,
  useRestoreFolderGroupFromTrashMutation,
  useSendFolderToTrashMutation,
  useUpdateFolderNameMutation,
  useLazyGetFolderByIdQuery,
  useSendFileToTrashMutation,
  useRestoreFileFromTrashMutation,
  useGetFilesFromTrashQuery,
  useSendFileGroupToTrashMutation,
  useRestoreFilesGroupFromTrashMutation,

  useDeleteFileGroupFromTrashMutation,
  useDeleteFolderGroupFromTrashMutation,

  useDeleteFileFromTrashMutation,
  useDeleteFolderFromTrashMutation,

  useLazyDownloadFolderAsZipQuery,
  useLazyDownloadFileQuery,

  useShareFolderMutation,
  useShareFileMutation,
  useStopShareFolderMutation,
  useStopShareFilesMutation,
  useLazyGetFolderSharedUsersQuery,
  useLazyGetFileSharedUsersQuery,
  useUploadMultiPartFileMutation,
  useUploadFilePartMutation,
  useCompletedUploadFileMutation,
  useCancelUploadFileMutation,
} = deskApi;
